import { useState, useEffect } from 'react';
import Navigation from "../home/components/Navigation";
import Footer from "../home/components/Footer";

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    location: '',
    service: [] as string[],
    message: '',
    photo: null as File | null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [photoPreview, setPhotoPreview] = useState<string>('');

  useEffect(() => {
    // Check if there's a hash in the URL
    if (window.location.hash === '#contact-form') {
      // Scroll to the form section
      setTimeout(() => {
        const formSection = document.getElementById('contact-form');
        if (formSection) {
          formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleServiceChange = (service: string) => {
    const currentServices = formData.service;
    if (currentServices.includes(service)) {
      setFormData({
        ...formData,
        service: currentServices.filter(s => s !== service),
      });
    } else {
      setFormData({
        ...formData,
        service: [...currentServices, service],
      });
    }
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Photo size must be less than 5MB');
        return;
      }
      setFormData({
        ...formData,
        photo: file,
      });
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setFormData({
      ...formData,
      photo: null,
    });
    setPhotoPreview('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.service.length === 0) {
      alert('Please select at least one service');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('company', formData.company);
      formBody.append('location', formData.location);
      formBody.append('service', formData.service.join(', '));
      formBody.append('message', formData.message);
      formBody.append('photo', formData.photo ? 'Uncollectable' : '');

      const response = await fetch('https://readdy.ai/api/form/d4riiocs30cg0huicrn0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          location: '',
          service: [],
          message: '',
          photo: null,
        });
        setPhotoPreview('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    'Lead Inspection',
    'Asbestos Survey',
    'IAQ Assessment',
    'Abatement Design',
    'Home Health Report',
    'Other',
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
            Get In Touch
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Contact LCA Group
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto">
            Corporate offices in New Jersey and Florida, serving clients across the region and nationwide.
          </p>
        </div>
      </section>

      {/* Contact & Offices Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            
            {/* Card 1 - Corporate Offices - Full Width Top */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow overflow-hidden md:col-span-2">
              <div className="p-8">
                <h2 className="text-2xl font-bold text-black mb-6">Corporate Offices</h2>
                
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Northeast Headquarters */}
                  <div>
                    <h3 className="font-semibold text-black mb-2">Northeast Headquarters</h3>
                    <p className="text-gray-600 text-sm mb-3">
                      Somerset, NJ 08873
                    </p>
                    <div className="w-full h-64 bg-gray-100 rounded-lg overflow-hidden">
                      <img
                        src="https://static.readdy.ai/image/a76fc84099da4ca5f9486c73960e2672/80ba2e01d0a812fbb92dcf5ae4aeb104.png"
                        alt="Somerset Office Building"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>

                  {/* Southeast Office */}
                  <div>
                    <h3 className="font-semibold text-black mb-2">Southeast Office</h3>
                    <p className="text-gray-600 text-sm mb-3">
                      Tampa, FL 33602
                    </p>
                    <div className="w-full h-64 bg-gray-100 rounded-lg overflow-hidden">
                      <img
                        src="https://static.readdy.ai/image/a76fc84099da4ca5f9486c73960e2672/63ade4593092a8dc7378b14d30471978.png"
                        alt="Tampa Office Building"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Card 2 - Contact Information */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-shadow">
              <h2 className="text-2xl font-bold text-black mb-6">Contact Information</h2>
              
              <div className="space-y-6">
                {/* Phone */}
                <div className="flex items-start">
                  <div className="flex items-center justify-center w-10 h-10 bg-gray-50 rounded-lg mr-4 flex-shrink-0">
                    <i className="ri-phone-line text-[#f59e0b] text-lg"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-black mb-1">Phone</div>
                    <a href="tel:7324189006" className="text-gray-600 hover:text-[#f59e0b] transition-colors cursor-pointer">
                      (732) 418-9006
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start">
                  <div className="flex items-center justify-center w-10 h-10 bg-gray-50 rounded-lg mr-4 flex-shrink-0">
                    <i className="ri-mail-line text-[#f59e0b] text-lg"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-black mb-1">Email</div>
                    <a href="mailto:contact@lcagroup.us" className="text-gray-600 hover:text-[#f59e0b] transition-colors cursor-pointer break-all">
                      contact@lcagroup.us
                    </a>
                  </div>
                </div>
              </div>

              <p className="text-gray-500 text-sm mt-6 pt-6 border-t border-gray-100">
                Business hours: Monday–Friday, 8:00 AM – 6:00 PM (ET)
              </p>
            </div>

            {/* Card 3 - Service Areas */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-shadow">
              <h2 className="text-2xl font-bold text-black mb-6">Service Areas</h2>
              
              <p className="text-gray-600 leading-relaxed">
                We proudly serve New Jersey, New York, Pennsylvania, Delaware, and Florida, with project work across the United States for portfolio, military housing, and institutional clients.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="py-16 sm:py-20 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4">Contact Us</h2>
              <p className="text-lg text-gray-600">
                Fill out the form below and we'll respond within one business day to discuss your environmental compliance needs.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 border border-gray-200">
              <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6" data-readdy-form>
                <div className="grid sm:grid-cols-2 gap-5 sm:gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f59e0b] focus:border-transparent text-sm"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f59e0b] focus:border-transparent text-sm"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5 sm:gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f59e0b] focus:border-transparent text-sm"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                      Company Name
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f59e0b] focus:border-transparent text-sm"
                      placeholder="Your Company"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="location" className="block text-sm font-semibold text-gray-700 mb-2">
                    Property Location *
                  </label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    required
                    value={formData.location}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f59e0b] focus:border-transparent text-sm"
                    placeholder="City, State"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Services Needed * (Select all that apply)
                  </label>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {services.map((service) => (
                      <label
                        key={service}
                        className="flex items-center p-3 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={formData.service.includes(service)}
                          onChange={() => handleServiceChange(service)}
                          className="w-4 h-4 text-[#f59e0b] border-gray-300 rounded focus:ring-[#f59e0b] cursor-pointer"
                        />
                        <span className="ml-3 text-sm text-gray-700">{service}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Project Details
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f59e0b] focus:border-transparent text-sm resize-none"
                    placeholder="Tell us about your project..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Attach Photo (Optional)
                  </label>
                  {!photoPreview ? (
                    <div className="relative">
                      <input
                        type="file"
                        id="photo"
                        accept="image/*"
                        onChange={handlePhotoChange}
                        className="hidden"
                      />
                      <label
                        htmlFor="photo"
                        className="flex items-center justify-center w-full px-4 py-8 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#f59e0b] transition-colors cursor-pointer bg-gray-50 hover:bg-gray-100"
                      >
                        <div className="text-center">
                          <i className="ri-image-add-line text-4xl text-gray-400 mb-2"></i>
                          <p className="text-sm text-gray-600 font-medium">Click to upload photo</p>
                          <p className="text-xs text-gray-500 mt-1">Max size: 5MB</p>
                        </div>
                      </label>
                    </div>
                  ) : (
                    <div className="relative">
                      <div className="relative w-full h-48 bg-gray-100 rounded-lg overflow-hidden">
                        <img
                          src={photoPreview}
                          alt="Preview"
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={removePhoto}
                        className="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors cursor-pointer"
                      >
                        <i className="ri-close-line text-lg"></i>
                      </button>
                      <p className="text-xs text-gray-600 mt-2">{formData.photo?.name}</p>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#f59e0b] text-white px-8 py-4 rounded-lg text-base font-semibold hover:bg-[#d97706] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap shadow-lg hover:shadow-xl"
                >
                  {isSubmitting ? 'Sending...' : 'Submit Request'}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                    Thank you! We'll contact you within one business day.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                    Something went wrong. Please try again or call us directly.
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
